import { Handle, Position } from '@xyflow/react';
import { Circle, Eye, Plus } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssigneeModel, EntityOrSuggestionRef, StepActionType } from '@bliks-io/contracts';
import { getBpmnEventIcon, type BpmnEventType } from '@bliks-io/ui';
import { StepCard } from '@/components/processes/StepCard';
import { resolveStepOutput } from '@/lib/review-modal-utils';
import { useResolvedAssignee } from '@/lib/useAssigneeLookup';

interface PredecessorStep {
  _id?: string;
  actionType?: StepActionType;
  task?: { taskDescription?: string; output?: string };
  rules?: Array<{ _id?: string; output?: string }>;
  // Unpopulated `{ type, id }` assignee ref — resolved against org lists below.
  assignedTo?: EntityOrSuggestionRef | null;
  assignedToModel?: AssigneeModel;
  generatedName?: string | null;
  generatedDescription?: string | null;
  [key: string]: unknown;
}

export interface StartEventNodeData {
  label: string;
  description?: string;
  eventType?: BpmnEventType;
  /**
   * Whether the start event carries a user-given name. `label` can't answer
   * this (it falls back to a placeholder), and eventType alone can't either:
   * 'none' is both the minted-empty placeholder AND a selectable BPMN type —
   * only name presence tells a configured none-event from a blank node.
   */
  hasName?: boolean;
  onCreateStartEvent?: () => void;
  predecessorStep?: PredecessorStep | 'START' | string; // Full populated step, 'START', or unpopulated ID
  ruleIds?: string[]; // Rule IDs for decision-based predecessor steps (full array)
  ruleId?: string; // Single rule ID for backward compatibility
}

// Shared style for transparent React Flow handles
const TRANSPARENT_HANDLE_STYLE = {
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'transparent',
  border: 'none',
  width: '10px',
  height: '10px'
} as const;

const StartEventNode = ({ data }: { data: StartEventNodeData }) => {
  const { t } = useTranslation();
  const EventIcon = data.eventType ? getBpmnEventIcon(data.eventType) : null;
  const hasEventInfo = !!data.eventType && (data.eventType !== 'none' || !!data.hasName);

  const predStep =
    typeof data.predecessorStep === 'object' &&
    data.predecessorStep !== null &&
    '_id' in data.predecessorStep
      ? (data.predecessorStep as PredecessorStep)
      : null;

  const resolvedAssignee = useResolvedAssignee(predStep?.assignedTo, predStep?.assignedToModel);

  const ruleIds = data.ruleIds || (data.ruleId ? [data.ruleId] : undefined);

  return (
    <div className="nopan flex flex-col items-center gap-2">
      {predStep ? (
        <div className="relative">
          <StepCard
            step={{
              actionType: predStep.actionType || 'task',
              task: predStep.task,
              assignedTo: resolvedAssignee,
              assignedToModel: predStep.assignedToModel,
              generatedName: predStep.generatedName,
              generatedDescription: predStep.generatedDescription
            }}
            output={resolveStepOutput(
              {
                actionType: predStep.actionType || 'task',
                task: predStep.task,
                rules: predStep.rules
              },
              ruleIds
            )}
          />
          <Handle
            id="source"
            type="source"
            position={Position.Right}
            style={TRANSPARENT_HANDLE_STYLE}
          />
        </div>
      ) : (
        <>
          <div className="relative">
            <div className="relative size-[42px] flex items-center justify-center">
              <Circle size={42} strokeWidth={1} fill="white" className="absolute text-fg-subtle" />
              {EventIcon && data.eventType !== 'none' && (
                <EventIcon size={20} className="z-10 text-fg-secondary" strokeWidth={1.5} />
              )}
            </div>
            <Handle
              id="source"
              type="source"
              position={Position.Right}
              style={TRANSPARENT_HANDLE_STYLE}
            />
          </div>
          <div className="text-sm text-center text-black w-[140px] break-words hyphens-auto">
            {data.label}
          </div>
        </>
      )}

      {data.onCreateStartEvent && (
        <div
          className="bg-white border border-normal rounded-xl px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-surface-subtle transition-colors"
          onClick={data.onCreateStartEvent}
        >
          <span className="text-base font-medium text-black">
            {hasEventInfo
              ? t('viralRecording.buttons.seeMoreAndChange')
              : t('viralRecording.buttons.createStartEvent')}
          </span>
          {hasEventInfo ? (
            <Eye size={17} className="text-black" />
          ) : (
            <Plus size={17} className="text-black" />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(StartEventNode);
